import { findNodePosByLocalIds } from '@atlaskit/editor-common/utils';
import { getPMFragmentWithFallback } from '../experience-application/get-pm-fragment-with-fallback';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { findActionItemsConfigItem } from '../prebuilt/config-items-empty/find-action-items/find-action-items';
import { summarizePageConfigItem } from '../prebuilt/config-items-empty/summarize-page/summarize-page';
import { addAnalytics } from '../analytics/utils';
const processStream = async (streaming, callback) => {
  for await (const streamItem of streaming) {
    // TODO: add in screens for when streaming or request fails
    // Can refer to platform/packages/editor/editor-plugin-ai/src/experience-application/streaming-service.ts
    if (streamItem.state === 'failed') {
      if ('apiName' in streamItem && streamItem.apiName === 'assistance-service') {
        callback('api fail', streamItem.state);
        continue;
      }
      if ('reason' in streamItem && streamItem.reason === 'backend-input-guard') {
        if (streamItem.guard === 'INPUT_EXCEEDS_TOKEN_LIMIT') {
          callback('token limit fail', streamItem.state);
          continue;
        }
        if (streamItem.guard === 'INPUT_TOO_SHORT_TO_SUMMARIZE' || streamItem.guard === 'INPUT_TOO_SHORT_TO_PROCESS') {
          callback('input too short fail', streamItem.state);
          continue;
        }
      }
      if ('reason' in streamItem && streamItem.reason === 'rate-limited') {
        callback('rate limited', streamItem.state);
        continue;
      }
      if ('reason' in streamItem && streamItem.reason === 'response-too-similar') {
        callback('response too similar', streamItem.state);
        continue;
      }
      callback('api failed', streamItem.state);
    } else if (streamItem.state === 'aup-violation') {
      callback('aup violation', streamItem.state);
      continue;
    } else {
      var _streamItem$data;
      callback((_streamItem$data = streamItem.data) === null || _streamItem$data === void 0 ? void 0 : _streamItem$data.content, streamItem.state);
      continue;
    }
  }
};
export const getAIBlockContext = ({
  configItem,
  intentSchemaId,
  editorView,
  providerFactory,
  idMap,
  intl,
  updateIdMap
}) => {
  var _configItem$getInitia;
  if (!editorView) {
    return;
  }
  const positions = [editorView.state.selection.$from.pos, editorView.state.selection.$to.pos];
  const initialContext = (_configItem$getInitia = configItem.getInitialContext) === null || _configItem$getInitia === void 0 ? void 0 : _configItem$getInitia.call(configItem, {
    editorView,
    positions,
    intl,
    updateIdMap,
    intentSchemaId
  });
  return {
    schema: editorView.state.schema,
    providerFactory,
    idMap,
    initialContext
  };
};
export const summarisePage = ({
  editorView,
  generativeAIApiUrl,
  product,
  callback,
  intl,
  initialContext
}) => {
  if (!editorView) {
    return;
  }
  const promptRequest = summarizePageConfigItem.triggerPromptRequest({
    initialContext: initialContext !== null && initialContext !== void 0 ? initialContext : {
      userLocale: 'en',
      intentSchemaId: 'summarize_intent_schema.json',
      document: editorView.state.doc.toString()
    },
    editorSchema: editorView.state.schema,
    formatMessage: intl.formatMessage,
    streamingListener: callback
  });
  const streaming = promptRequest({
    generativeAIApiUrl,
    product,
    abortController: new AbortController()
  });
  processStream(streaming, callback);
};
export const findActionItems = ({
  editorView,
  generativeAIApiUrl,
  product,
  callback,
  intl,
  initialContext
}) => {
  if (!editorView) {
    return;
  }
  const promptRequest = findActionItemsConfigItem.triggerPromptRequest({
    initialContext: initialContext !== null && initialContext !== void 0 ? initialContext : {
      userLocale: 'en',
      intentSchemaId: 'action_items_intent_schema.json',
      document: editorView.state.doc.toString()
    },
    editorSchema: editorView.state.schema,
    formatMessage: intl.formatMessage,
    streamingListener: callback
  });
  const streaming = promptRequest({
    generativeAIApiUrl,
    product,
    abortController: new AbortController()
  });
  processStream(streaming, callback);
};
export const regenerateExtension = ({
  editorView,
  localId,
  extensionKey,
  idMap
}) => {
  const tr = editorView === null || editorView === void 0 ? void 0 : editorView.state.tr;
  if (editorView && tr) {
    addAnalytics({
      editorState: editorView === null || editorView === void 0 ? void 0 : editorView.state,
      tr,
      payload: {
        action: 'click',
        actionSubject: 'editorPluginAI',
        actionSubjectId: 'AIBlocks',
        eventType: 'ui',
        attributes: {
          extensionType: 'com.atlassian.ai-blocks',
          extensionKey: extensionKey,
          action: 'regenerate'
        }
      }
    });
  }
  convertExtension({
    editorView,
    localId,
    extensionKey,
    idMap,
    tr
  });
};
export const convertExtension = ({
  editorView,
  localId,
  extensionKey,
  idMap,
  tr
}) => {
  if (!editorView) {
    return;
  }
  const nodes = findNodePosByLocalIds(editorView.state, [localId]);
  if (!tr) {
    tr = editorView.state.tr;
  }
  const nodeToReplace = nodes[0];
  if (!nodeToReplace) {
    return;
  }
  const {
    pmFragment
  } = getPMFragmentWithFallback({
    markdown: nodeToReplace.node.attrs.parameters.content,
    schema: editorView.state.schema,
    source: 'ai-blocks',
    fireAIAnalyticsEvent: () => {},
    idMap
  });
  let newNode;
  if (extensionKey.includes('BodiedExtension')) {
    newNode = editorView.state.schema.node('bodiedExtension', {
      ...nodeToReplace.node.attrs,
      extensionType: 'com.atlassian.ai-blocks',
      extensionKey
    }, pmFragment);
    tr.replaceWith(nodeToReplace.pos, nodeToReplace.pos + nodeToReplace.node.nodeSize, newNode);
    editorView.dispatch(tr);
  } else {
    // We want to delete the original node first and then create the new node and insert it
    // This way if we call regenerate whilst on the extension view, the state will be updated correctly
    // and retrigger the regenerate
    let tr = editorView.state.tr.delete(nodeToReplace.pos, nodeToReplace.pos + nodeToReplace.node.nodeSize);
    editorView.dispatch(tr);
    newNode = editorView.state.schema.node('extension', {
      ...nodeToReplace.node.attrs,
      extensionType: 'com.atlassian.ai-blocks',
      extensionKey,
      parameters: {
        ...nodeToReplace.node.attrs.parameters,
        content: ''
      }
    }, Fragment.from([]));
    tr = editorView.state.tr.insert(nodeToReplace.pos, newNode);
    editorView.dispatch(tr);
  }
};